import mockReportService from './mock-report.service'
import reportService from './report.service'
import configService from './config.service'

/**
 * Report service wrapper that switches between mock and real API based on configuration
 */
class ReportServiceWrapper {
  private _service: any
  private _initialized: boolean = false
  private _initPromise: Promise<void> | null = null

  constructor() {
    this._service = reportService // Default to real API
  }

  private async _initialize(): Promise<void> {
    if (this._initPromise) {
      return this._initPromise
    }

    this._initPromise = this._doInitialize()
    return this._initPromise
  }

  private async _doInitialize(): Promise<void> {
    try {
      const config = await configService.getConfig()
      const useMocks = (config as any).USE_MOCK_SERVICES
      
      console.log('Report service configuration:', {
        useMocks,
        source: 'config.json'
      })

      // Set the appropriate service
      this._service = useMocks ? mockReportService : reportService
      this._initialized = true

      console.log('Using report service:', useMocks ? 'mock report service' : 'real API')
    } catch (error) {
      console.error('Error initializing report service:', error)
      // Keep default service (real API) on error
      this._initialized = true
    }
  }

  /**
   * Proxy method calls to the underlying service
   */
  private async _proxyCall(methodName: string, args: any[]): Promise<any> {
    if (!this._initialized) {
      await this._initialize()
    }
    const method = this._service[methodName]
    if (typeof method === 'function') {
      return method.apply(this._service, args)
    }
    throw new Error(`Method ${methodName} not found on service`)
  }

  // Expose the main service methods
  async getAllReports(...args: any[]): Promise<any> {
    return this._proxyCall('getAllReports', args)
  }

  async getReportById(...args: any[]): Promise<any> {
    return this._proxyCall('getReportById', args)
  }

  async createReport(...args: any[]): Promise<any> {
    return this._proxyCall('createReport', args)
  }

  async updateReport(...args: any[]): Promise<any> {
    return this._proxyCall('updateReport', args)
  }

  async deleteReport(...args: any[]): Promise<any> {
    return this._proxyCall('deleteReport', args)
  }

  async generateReport(...args: any[]): Promise<any> {
    return this._proxyCall('generateReport', args)
  }

  async getBudgetConsumptionReport(...args: any[]): Promise<any> {
    return this._proxyCall('getBudgetConsumptionReport', args)
  }

  async getTaskBudgetReport(...args: any[]): Promise<any> {
    return this._proxyCall('getTaskBudgetReport', args)
  }

  async getIntervenantBudgetReport(...args: any[]): Promise<any> {
    return this._proxyCall('getIntervenantBudgetReport', args)
  }

  async getTaskBudgetDetails(...args: any[]): Promise<any> {
    return this._proxyCall('getTaskBudgetDetails', args)
  }

  async exportBudgetReportPDF(...args: any[]): Promise<any> {
    return this._proxyCall('exportBudgetReportPDF', args)
  }

  async exportBudgetReportExcel(...args: any[]): Promise<any> {
    return this._proxyCall('exportBudgetReportExcel', args)
  }
}

const reportServiceInstance = new ReportServiceWrapper()

console.log('ReportService instance methods:', Object.getOwnPropertyNames(Object.getPrototypeOf(reportServiceInstance)))

export default reportServiceInstance
